import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/useLocale';
import { FOOTER_SEO_GROUPS } from '../../config/internalLinks';
import { SOCIAL_LINKS } from '../../config/socialLinks';
import '../../styles/landing.scss';

function landingFooterLabel(page) {
  if (typeof page !== 'object' || !page) return null;
  if (page.heroTitle) return page.heroTitle.split('\n')[0];
  if (page.seoTitle) return page.seoTitle.split('|')[0].trim();
  return null;
}

function Footer() {
  const { t } = useTranslation();
  const { localizedPath, barePath } = useLocale();
  const homePath = localizedPath('/');
  const featuresHref = barePath === '/' ? '#features' : `${homePath}#features`;

  return (
    <footer className="footer" role="contentinfo">
      <Container>
        <Row className="footer-grid g-4 g-lg-5">
          <Col xs={12} sm={6} lg={4} xl={3}>
            <nav className="footer-col" aria-label={t('footer.sections.platform')}>
              <h2 className="footer-col-title">{t('footer.sections.platform')}</h2>
              <ul className="footer-nav-list">
                <li>
                  <a href={featuresHref}>{t('footer.features')}</a>
                </li>
                <li>
                  <Link to={localizedPath('/help')}>{t('footer.guides')}</Link>
                </li>
                <li>
                  <Link to={localizedPath('/faq')}>{t('footer.faq')}</Link>
                </li>
                <li>
                  <Link to={localizedPath('/a-propos')}>{t('footer.about')}</Link>
                </li>
                <li>
                  <Link to={localizedPath('/references')}>{t('footer.references')}</Link>
                </li>
              </ul>
            </nav>
          </Col>

          {FOOTER_SEO_GROUPS.map((group) => (
            <Col xs={12} sm={6} lg={4} xl={3} key={group.id}>
              <nav className="footer-col" aria-label={t(`landing.seoPagesGroups.${group.id}`)}>
                <h2 className="footer-col-title">{t(`landing.seoPagesGroups.${group.id}`)}</h2>
                <ul className="footer-nav-list">
                  {group.pageKeys.map((pageKey) => {
                    const page = t(`landingPagesTeasers.${pageKey}`, { returnObjects: true });
                    const path = typeof page === 'object' && page?.path ? page.path : null;
                    const label = landingFooterLabel(page);
                    if (!path || !label) return null;
                    return (
                      <li key={pageKey}>
                        <Link to={localizedPath(path)}>{label}</Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </Col>
          ))}

          <Col xs={12} sm={6} lg={4} xl={3}>
            <nav className="footer-col" aria-label={t('footer.sections.legal')}>
              <h2 className="footer-col-title">{t('footer.sections.legal')}</h2>
              <ul className="footer-nav-list">
                <li>
                  <Link to={localizedPath('/terms')}>{t('footer.terms')}</Link>
                </li>
                <li>
                  <Link to={localizedPath('/privacy')}>{t('footer.privacy')}</Link>
                </li>
              </ul>
            </nav>
          </Col>
        </Row>

        <div className="footer-bottom">
          <nav className="footer-social" aria-label={t('footer.sections.social')}>
            <ul className="footer-social-list">
              {SOCIAL_LINKS.map(({ id, href, network, labelKey }) => (
                <li key={id}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t(labelKey)}
                    title={t(labelKey)}
                  >
                    <i className={`bi bi-${network}`} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <p className="copyright mb-0">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </Container>
    </footer>
  );
}

export default Footer;
